import { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SessionUser } from '@adco-rooms/shared';
import { api, isApiError } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/query-client';
import { isAuthSettled, useAuth } from '@/lib/auth/auth-provider';

/**
 * Application session.
 *
 * Authentication (a valid Entra token) is not authorization: the user must also
 * exist and be active in the local `users` table. `GET /auth/me` is the single
 * place that decides, and a 403 sends the user to the "sin acceso" screen.
 */

export interface SessionContextValue {
  user: SessionUser | null;
  isAdmin: boolean;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const { status: authStatus } = useAuth();
  const enabled = isAuthSettled(authStatus);

  const query = useQuery({
    queryKey: queryKeys.session(),
    queryFn: ({ signal }) => api.get<SessionUser>('/auth/me', { signal }),
    enabled,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => {
      if (isApiError(error) && error.isClientError) return false;
      return failureCount < 2;
    },
  });

  const { error } = query;

  // A user authenticated in Entra but not authorized locally lands on /403.
  useEffect(() => {
    if (isApiError(error) && error.isForbidden) {
      navigate('/403', { replace: true });
    }
  }, [error, navigate]);

  const value = useMemo<SessionContextValue>(
    () => ({
      user: query.data ?? null,
      isAdmin: query.data?.isAdmin ?? false,
      // While the token is still being acquired the session is "loading" too.
      isLoading: !enabled || query.isPending,
      error: query.error,
      refetch: () => void query.refetch(),
    }),
    [enabled, query],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession debe usarse dentro de <SessionProvider>.');
  }
  return context;
}
